import vercelBlobService from './vercelBlobService';
import optimizedVercelBlobService from './optimizedVercelBlobService';
import supabaseService from './supabase';
import firebaseMetadataService from './firebaseMetadataService';

class HybridImageService {
  constructor() {
    this.preferredStorage = 'vercel-blob'; // 'vercel-blob' or 'supabase'
    this.fallbackEnabled = true;
    console.log('🚀 HybridImageService initialized with Vercel Blob as primary storage');
  }

  /**
   * Set preferred storage provider
   * @param {string} provider - 'vercel-blob' or 'supabase'
   */
  setPreferredStorage(provider) {
    this.preferredStorage = provider;
  }

  /**
   * Get all product images from preferred storage with fallback
   * @returns {Promise<Array>} Array of image objects
   */
  async getAllImages() {
    try {
      if (this.preferredStorage === 'vercel-blob') {
        return await this.getImagesFromVercelBlob();
      } else {
        return await this.getImagesFromSupabase();
      }
    } catch (error) {
      console.error(`Error getting images from ${this.preferredStorage}:`, error);

      if (this.fallbackEnabled) {
        console.log('Attempting fallback storage...');
        try {
          if (this.preferredStorage === 'vercel-blob') {
            return await this.getImagesFromSupabase();
          } else {
            return await this.getImagesFromVercelBlob();
          }
        } catch (fallbackError) {
          console.error('Fallback storage also failed:', fallbackError);
          return [];
        }
      }

      return [];
    }
  }

  /**
   * Get all images from both storage providers combined (OPTIMIZED)
   * Uses optimized Vercel Blob service with caching
   * @returns {Promise<Array>} Combined array of images from all sources
   */
  async getAllImagesFromBothSources() {
    try {
      console.log('🚀 Loading images from all storage providers (OPTIMIZED)...');

      const allImages = [];

      // Load from Optimized Vercel Blob (with caching)
      try {
        console.log('📦 Loading Vercel Blob images (OPTIMIZED)...');
        const vercelImages = await optimizedVercelBlobService.getImagesWithMetadata();
        if (vercelImages && Array.isArray(vercelImages)) {
          allImages.push(...vercelImages);
          console.log(`✅ Loaded ${vercelImages.length} images from Vercel Blob (cached)`);
        }
      } catch (error) {
        console.warn('⚠️ Failed to load optimized Vercel Blob images, falling back to standard service:', error);
        try {
          const vercelImages = await this.getImagesFromVercelBlob();
          if (vercelImages && Array.isArray(vercelImages)) {
            allImages.push(...vercelImages);
            console.log(`✅ Loaded ${vercelImages.length} images from Vercel Blob (fallback)`);
          }
        } catch (fallbackError) {
          console.warn('⚠️ Fallback also failed, continuing without Vercel Blob images:', fallbackError);
        }
      }

      // Load from Supabase
      try {
        console.log('🗄️ Loading Supabase images...');
        const supabaseImages = await this.getImagesFromSupabase();
        if (supabaseImages && Array.isArray(supabaseImages)) {
          allImages.push(...supabaseImages);
          console.log(`✅ Loaded ${supabaseImages.length} images from Supabase`);
        }
      } catch (error) {
        console.warn('⚠️ Failed to load Supabase images, continuing without them:', error);
      }

      // Remove duplicates based on filename
      const uniqueImages = this.removeDuplicates(allImages);

      console.log(`🎯 Total unique images loaded: ${uniqueImages.length}`);

      // Preload images for better performance
      try {
        const imageUrls = uniqueImages.map(img => img.publicUrl || img.url).filter(Boolean);
        if (imageUrls.length > 0) {
          optimizedVercelBlobService.preloadImages(imageUrls.slice(0, 20)); // Preload first 20 images
        }
      } catch (preloadError) {
        console.warn('⚠️ Failed to preload images:', preloadError);
      }

      return uniqueImages;

    } catch (error) {
      console.error('❌ Error loading hybrid images:', error);
      return [];
    }
  }

  /**
   * Remove duplicate images based on filename
   * @param {Array} images - Array of images
   * @returns {Array} Array of unique images
   */
  removeDuplicates(images) {
    const seen = new Set();
    const unique = [];

    for (const image of images) {
      const key = image.name.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(image);
      }
    }

    return unique;
  }

  /**
   * Get images from Vercel Blob storage
   * @returns {Promise<Array>} Array of image objects
   */
  async getImagesFromVercelBlob() {
    try {
      const blobImages = await vercelBlobService.getProductImages();
      const imagesWithMetadata = [];

      for (const blobImage of blobImages) {
        try {
          // Extract filename from pathname
          const filename = blobImage.pathname.split('/').pop();

          // Get metadata from Firebase
          let metadata = null;
          try {
            metadata = await firebaseMetadataService.getImageMetadata(filename);
          } catch (metadataError) {
            console.log(`No Firebase metadata for Vercel Blob image: ${filename}`);
          }

          // Only include images with metadata (admin-added products)
          if (metadata && metadata.title && metadata.title.trim()) {
            const imageObject = {
              id: filename.replace(/\.[^/.]+$/, ""), // Remove file extension for ID
              name: filename,
              url: blobImage.url,
              downloadUrl: blobImage.downloadUrl,
              size: blobImage.size,
              uploadedAt: blobImage.uploadedAt,
              storage: 'vercel-blob',
              // Metadata from Firebase
              title: metadata.title,
              description: metadata.description || '',
              category: metadata.category || 'Uncategorized',
              price: metadata.price || null,
              inStock: metadata.in_stock !== false,
              featured: metadata.featured || false,
              hasMetadata: true,
              tags: metadata.tags || [],
              specifications: metadata.specifications || {}
            };

            imagesWithMetadata.push(imageObject);
          }
        } catch (imageError) {
          console.error(`Error processing Vercel Blob image ${blobImage.pathname}:`, imageError);
          // Continue with next image
        }
      }

      console.log(`✅ Loaded ${imagesWithMetadata.length} images from Vercel Blob`);
      return imagesWithMetadata;
    } catch (error) {
      console.error('Error getting images from Vercel Blob:', error);
      // Return empty array instead of throwing to allow fallback
      return [];
    }
  }

  /**
   * Get images from Supabase storage
   * @returns {Promise<Array>} Array of image objects
   */
  async getImagesFromSupabase() {
    try {
      const imageList = await supabaseService.listImages('products');
      const imagesWithMetadata = [];

      if (imageList && imageList.length > 0) {
        for (const image of imageList) {
          try {
            const publicUrl = supabaseService.getPublicUrl(`products/${image.name}`);

            // Get metadata from Firebase
            let metadata = null;
            try {
              metadata = await firebaseMetadataService.getImageMetadata(image.name);
            } catch (metadataError) {
              console.log(`No Firebase metadata for Supabase image: ${image.name}`);
            }

            // Only include images with metadata (admin-added products)
            if (metadata && metadata.title && metadata.title.trim()) {
              const imageObject = {
                ...image,
                id: image.name.replace(/\.[^/.]+$/, ""), // Remove file extension for ID
                name: image.name,
                url: publicUrl,
                downloadUrl: publicUrl,
                publicUrl,
                fullPath: `products/${image.name}`,
                size: image.size || 0,
                uploadedAt: image.updated_at,
                storage: 'supabase',
                // Metadata from Firebase
                title: metadata.title,
                description: metadata.description || '',
                category: metadata.category || 'Uncategorized',
                price: metadata.price || null,
                inStock: metadata.in_stock !== false,
                featured: metadata.featured || false,
                hasMetadata: true,
                tags: metadata.tags || [],
                specifications: metadata.specifications || {}
              };

              imagesWithMetadata.push(imageObject);
            }
          } catch (imageError) {
            console.error(`Error processing Supabase image ${image.name}:`, imageError);
            // Continue with next image
          }
        }
      }

      console.log(`✅ Loaded ${imagesWithMetadata.length} images from Supabase`);
      return imagesWithMetadata;
    } catch (error) {
      console.error('Error getting images from Supabase:', error);
      // Return empty array instead of throwing to allow fallback
      return [];
    }
  }

  /**
   * Get all images from both storage providers
   * @returns {Promise<Array>} Combined array of all images
   */
  async getAllImages() {
    try {
      const [supabaseImages, vercelBlobImages] = await Promise.allSettled([
        this.getImagesFromSupabase(),
        this.getImagesFromVercelBlob()
      ]);

      let allImages = [];

      // Process Supabase images
      if (supabaseImages.status === 'fulfilled' && supabaseImages.value) {
        allImages = [...allImages, ...supabaseImages.value];
      }

      // Process Vercel Blob images
      if (vercelBlobImages.status === 'fulfilled' && vercelBlobImages.value) {
        allImages = [...allImages, ...vercelBlobImages.value];
      }

      // Remove duplicates based on filename (prefer Vercel Blob)
      const uniqueImages = allImages.reduce((acc, current) => {
        const existing = acc.find(item => item.name === current.name);
        if (!existing) {
          acc.push(current);
        } else if (current.storage === 'vercel-blob' && existing.storage === 'supabase') {
          const index = acc.findIndex(item => item.name === current.name);
          acc[index] = current;
        }
        return acc;
      }, []);

      return uniqueImages;
    } catch (error) {
      console.error('Error getting all images:', error);
      return [];
    }
  }

  /**
   * Upload image to preferred storage
   * @param {File} file - The image file
   * @param {string} filename - Desired filename
   * @param {Object} metadata - Image metadata
   * @returns {Promise<Object>} Upload result
   */
  async uploadImage(file, filename, metadata = {}) {
    try {
      let uploadResult;

      if (this.preferredStorage === 'vercel-blob') {
        uploadResult = await vercelBlobService.uploadProductImage(file, filename);
      } else {
        // Upload to Supabase
        const { data, error } = await supabaseService
          .storage
          .from('products')
          .upload(filename, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (error) throw error;

        const { data: publicUrl } = supabaseService
          .storage
          .from('products')
          .getPublicUrl(filename);

        uploadResult = {
          success: true,
          url: publicUrl.publicUrl,
          pathname: filename
        };
      }

      if (uploadResult.success) {
        // Save metadata to Firebase
        await firebaseMetadataService.saveImageMetadata(filename, {
          ...metadata,
          storage_provider: this.preferredStorage,
          uploaded_at: new Date().toISOString()
        });
      }

      return uploadResult;
    } catch (error) {
      console.error('Error uploading image:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete image from storage
   * @param {string} filename - The filename to delete
   * @param {string} storageProvider - Which storage to delete from
   * @returns {Promise<Object>} Deletion result
   */
  async deleteImage(filename, storageProvider = null) {
    const provider = storageProvider || this.preferredStorage;
    
    try {
      if (provider === 'vercel-blob') {
        // For Vercel Blob, we need the full URL
        const images = await vercelBlobService.getProductImages();
        const image = images.find(img => img.pathname.endsWith(filename));
        
        if (image) {
          return await vercelBlobService.deleteFile(image.url);
        } else {
          throw new Error('Image not found in Vercel Blob');
        }
      } else {
        // Delete from Supabase
        const { error } = await supabaseService
          .storage
          .from('products')
          .remove([filename]);

        if (error) throw error;

        return {
          success: true,
          message: 'Image deleted successfully'
        };
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Check if file is an image
   * @param {string} filename - File name
   * @returns {boolean} Whether file is an image
   */
  isImageFile(filename) {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  }

  /**
   * Generate title from filename
   * @param {string} filename - The filename
   * @returns {string} Generated title
   */
  generateTitleFromFilename(filename) {
    return filename
      .replace(/\.[^/.]+$/, '') // Remove extension
      .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
  }

  /**
   * Get storage statistics
   * @returns {Promise<Object>} Storage statistics
   */
  async getStorageStats() {
    try {
      const [vercelImages, supabaseImages] = await Promise.all([
        this.getImagesFromVercelBlob().catch(() => []),
        this.getImagesFromSupabase().catch(() => [])
      ]);

      return {
        vercelBlob: {
          count: vercelImages.length,
          totalSize: vercelImages.reduce((sum, img) => sum + (img.size || 0), 0)
        },
        supabase: {
          count: supabaseImages.length,
          totalSize: supabaseImages.reduce((sum, img) => sum + (img.size || 0), 0)
        },
        preferred: this.preferredStorage,
        fallbackEnabled: this.fallbackEnabled
      };
    } catch (error) {
      console.error('Error getting storage stats:', error);
      return {
        error: error.message
      };
    }
  }

  /**
   * Switch to Vercel Blob as primary storage
   */
  switchToVercelBlob() {
    this.preferredStorage = 'vercel-blob';
    console.log('✅ Switched to Vercel Blob as primary storage');
  }

  /**
   * Switch to Supabase as primary storage
   */
  switchToSupabase() {
    this.preferredStorage = 'supabase';
    console.log('✅ Switched to Supabase as primary storage');
  }
}

// Create and export singleton instance
const hybridImageService = new HybridImageService();
export default hybridImageService;
